import {DeleteOutline} from "@mui/icons-material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import {Button, CircularProgress, Divider, Grid, IconButton, Stack, Typography} from "@mui/material";
import Box from "@mui/material/Box";
import {IconAlertTriangle, IconCamera, IconCheck, IconCircleOff, IconMicrophone, IconRefresh} from "@tabler/icons-react";
import {useAtom} from "jotai";
import React, {useEffect, useRef, useState} from "react";
import Camera, {FACING_MODES, IMAGE_TYPES} from "react-html5-camera-photo";
import 'react-html5-camera-photo/build/css/index.css';
import {toast} from "react-toastify";
import {checkPermissionStatus, removeFile} from "../../../utils/functions";
import {AnswerAtom} from "../atoms";
import {grey} from "@mui/material/colors";
import useConfirm from "../../../hooks/useConfirm";

export const ImageQuestion = () => {
  const [answer, setAnswer] = useAtom(AnswerAtom);
  const [loading, setLoading] = useState(false);
  const [facingMode, setFacingMode] = useState(FACING_MODES.USER);
  const [cameraError, setCameraError] = useState(null);
  const streamRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    const checkPermission = async () => {
      const status = await checkPermissionStatus("camera");
      console.log('status', status)
      if (status === "granted") {
        setShowCamera(true);
        return;
      }
    }
    checkPermission();
  }, []);

  useEffect(() => {
    if (streamRef.current) {
      return () => {
        onCameraStop();
      }
    }
  }, [streamRef.current]);

  const handleCheckPermission = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const status = await checkPermissionStatus("camera");
    console.log('status', status);

    if (status === "granted") {
      setShowCamera(true);
      return;
    }

    const confirmBtn = (
      <Stack direction={"row"} gap={2} alignItems={"center"} sx={{ color: "primary.main" }}>
        <IconCheck size={18} />
        <Typography fontSize={"0.9em"} color={"primary.main"}>OK</Typography>
      </Stack>
    );
    const cancelBtn = (
      <Stack direction={"row"} gap={2} alignItems={"center"} sx={{ color: "error.main" }}>
        <IconCircleOff size={18} />
        <Typography fontSize={"0.9em"} color={"error.main"}>Deny</Typography>
      </Stack>
    );
    const confirmStyle = {
      bgcolor: "transparent",
      color: "error",
      flex: 1,
      boxShadow: 0,
      border: 0,
      borderColor: "primary.main",
      "&:hover": { bgcolor: grey[50], boxShadow: 0, borderColor: "primary.main" },
    };
    const cancelStyle = {
      bgcolor: "transparent",
      color: "error",
      flex: 1,
      boxShadow: 0,
      border: 0,
      borderColor: "error.main",
      "&:hover": { bgcolor: grey[50], boxShadow: 0, borderColor: "error.main" },
    };

    const response = await confirm(
      <Stack direction={"row"} gap={2} alignItems={"center"} sx={{ color: "primary.main" }}>
        <IconCamera size={18} />
        <Typography fontSize={"0.9em"} color={"primary.main"}>Enable camera access</Typography>
      </Stack>,
      "Enable camera access for this survey",
      confirmBtn,
      cancelBtn,
      confirmStyle,
      cancelStyle,
    );

    if (response) {
      setShowCamera(true);
      return;
    }

    // Wait 500ms before showing second modal
    await new Promise(res => setTimeout(res, 500));

    const secondConfirmBtn = (
      <Stack direction={"row"} gap={2} alignItems={"center"} sx={{ color: "primary.main" }}>
        <IconCheck size={18} />
        <Typography fontSize={"0.9em"} color={"primary.main"}>Allow</Typography>
      </Stack>
    );

    const secondResponse = await confirm(
      <Stack direction={"row"} gap={2} alignItems={"center"} sx={{ color: "primary.main" }}>
        <Typography fontSize={"0.9em"} fontWeight={600} color={"primary.main"}>
          Are you sure?
        </Typography>
      </Stack>,
      "Re-enabling camera access is a difficult process",
      secondConfirmBtn,
      cancelBtn,
      confirmStyle,
      cancelStyle,
    );

    if (secondResponse) {
      setShowCamera(true);
      return;
    }

    setShowCamera(false);
  };



  const onTakePhoto = async (dataUri) => {
    if (loading) {
      return;
    }
    setAnswer(prev => {
      const photos = prev?.photos ? [...prev?.photos] : [];
      photos.unshift(dataUri);
      if (photos.length > 3) {
        photos.pop();
      }
      return {
        ...prev,
        photos: photos
      }
    })
  }

  const onRemovePhoto = async (photo) => {
    try {
      setLoading(true);
      await removeFile(photo);
    } catch (error) {
      console.log("Failed to remove photo:", photo)
      console.error(error);
    } finally {
      setLoading(false);
    }
    setAnswer(prev => ({...prev, photos: prev?.photos?.filter(i => i !== photo)}));
  }

  const onSelectImageFile = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataURI = e.target.result;
      await onTakePhoto(dataURI);
    };
    reader.readAsDataURL(file);
  }

  function onCameraError(error) {
    toast.error("Permission denied, please allow camera access in settings");
    setCameraError("Permission denied, please allow camera access in settings");
    console.error('onCameraError', "Permission denied, please allow camera access in settings");
  }

  function onCameraStart(s) {
    console.log('onCameraStart');
    streamRef.current = s;
  }

  function onCameraStop() {
    console.log('onCameraStop');
    if (streamRef?.current) {
      const tracks = streamRef?.current?.getTracks()
      console.log("Stop tracks", tracks)
      tracks.forEach(function (track) {
        console.log("Track stopped", track)
        track.stop();
      })
      streamRef.current = null;
    }
  }

  return (
    <>
      <Box>
        {
          showCamera ? <Box maxWidth={"sm"} sx={{position: "relative", mx: "auto"}}>
              {loading && <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 100000000
                }}><CircularProgress/></Box>}
              {
                cameraError ?
                  <Box sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 100000000
                  }}><Typography variant="body2" color="error">{cameraError}</Typography></Box>
                  :
                  <Camera
                    onTakePhoto={(dataUri) => onTakePhoto(dataUri)}
                    onCameraError={onCameraError}
                    style={{width: "100% !important", height: "auto"}}
                    idealFacingMode={facingMode}
                    idealResolution={{width: 350, height: 350}}
                    imageType={IMAGE_TYPES.JPG}
                    imageCompression={0.97}
                    isMaxResolution={false}
                    isImageMirror={facingMode === FACING_MODES.USER}
                    isDisplayStartCameraError={false}
                    sizeFactor={1}
                    onCameraStart={onCameraStart}
                    onCameraStop={onCameraStop}

                  />
              }
              <IconButton
                component="label"
                size={'large'} disabled={loading}
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  transform: "translate(25%, -30%)",
                  bgcolor: "rgba(255,255,255,0.1)",
                  color: 'white'
                }}
              >
                <AddPhotoAlternateIcon/>
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={onSelectImageFile}
                />
              </IconButton>
              <IconButton
                size={'large'} disabled={loading}
                onClick={() => setFacingMode(prev => prev === FACING_MODES.ENVIRONMENT ? FACING_MODES.USER : FACING_MODES.ENVIRONMENT)}
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  transform: "translate(-25%, -30%)",
                  bgcolor: "rgba(255,255,255,0.1)",
                  color: 'white'
                }}
              >
                <IconRefresh/>
              </IconButton>
            </Box>
            : <Stack direction={"column"} gap={1}>
              <Typography fontSize={"0.9em"} align={"center"} color={grey[500]} fontStyle={"italic"}>To answer this question, camera access is required. Please allow camera permissions for a smooth experience.</Typography>
              <Button
                variant={"outlined"}
                onClick={handleCheckPermission}
                sx={{mx: "auto", minWidth: "300px"}}
                startIcon={<IconCamera size={18} />}
              >
                Allow Camera Permission
              </Button>
              <Divider>OR</Divider>
              <Button
                component="label"
                size={'large'} disabled={loading}
                startIcon={<AddPhotoAlternateIcon/>}
                color={"secondary"}
                sx={{mx: "auto", minWidth: "300px"}}
              >
                Choose from Files
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={onSelectImageFile}
                />
              </Button>
            </Stack>
        }

        <Grid container justifyContent={'center'} sx={{marginTop: 2}}>
          {answer?.photos?.map((photo) => {
            return (
              <Grid key={photo} item sx={{position: 'relative', display: 'inline-flex', margin: 0.5}}>
                {loading && <Box sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 100000000
                }}><CircularProgress size={15}/></Box>}
                <img src={photo} style={{width: 90, height: 90, objectFit: "cover", filter: loading ? "grayscale(0.9)" : "unset"}}
                     alt={`preview img`}/>
                {loading === false && <IconButton
                  sx={{
                    color: 'white',
                    position: "absolute",
                    top: "0", right: "0",
                    transform: "translate(-3px, 3px)", zIndex: 1,
                    bgcolor: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(4px)",
                  }}
                  size={"small"}
                  disabled={loading}
                  onClick={() => onRemovePhoto(photo)}
                >
                  <DeleteOutline fontSize={"small"}/>
                </IconButton>}
              </Grid>)
          })}
        </Grid>
        {answer?.photos?.length >= 3 && <Typography align={"center"} variant={"body2"} sx={{color: "warning.main"}}>
          <IconAlertTriangle size={20} style={{paddingTop: "8px"}}/>
          You are only allowed to upload a maximum of 3 images.
          {/*If you attempt to upload more than 3 images, your upload*/}
          {/*may not be processed.*/}
        </Typography>}
      </Box>
    </>
  )
}